import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute } from './protected-route';
import { RoleRoute } from './role-route';
import NotFound from '@/pages/NotFound';
import SlugRouteResolver from '@/pages/SlugRouteResolver';
import { useAuth } from '@/hooks/useAuth';

// Auth pages
import LoginPage from '@/features/auth/LoginPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/ResetPasswordPage';
import SignUpPage from '@/features/auth/SignUpPage';
import AdoptPage from '@/pages/AdoptPage';
import ProfilePage from '@/pages/ProfilePage';
import WebappEntryPage from '@/pages/WebappEntryPage';
import AuthHandoffPage from '@/pages/AuthHandoffPage';
import DataPolicyPage from '@/pages/DataPolicyPage';

// Protected pages
import Dashboard from '@/pages/Dashboard';
import AnimalsListPage from '@/features/animals/list/AnimalsListPage';
import AnimalFormPage from '@/features/animals/form/AnimalFormPage';
import RescuedAnimalsListPage from '@/features/rescued-animals/list/RescuedAnimalsListPage';
import RescuedAnimalFormPage from '@/features/rescued-animals/form/RescuedAnimalFormPage';
import RescuedAnimalsPage from '@/pages/RescuedAnimalsPage';
import RescuedAnimalDetailPage from '@/pages/RescuedAnimalDetailPage';
import SpeciesPage from '@/features/lookups/species/SpeciesPage';
import SexPage from '@/features/lookups/sex/SexPage';
import SizePage from '@/features/lookups/size/SizePage';
import StatusPage from '@/features/lookups/status/StatusPage';
import ColorsPage from '@/features/lookups/colors/ColorsPage';
import SheltersListPage from '@/features/shelters/list/SheltersListPage';
import ShelterFormPage from '@/features/shelters/form/ShelterFormPage';
import UsersListPage from '@/features/users/list/UsersListPage';
import UserFormPage from '@/features/users/form/UserFormPage';
import BannersAdminPage from '@/features/banners/admin/BannersAdminPage';
import FooterAdminPage from '@/features/footer/admin/FooterAdminPage';
import FormTemplatesAdminPage from '@/features/form-templates/admin/FormTemplatesAdminPage';
import AdoptionWaitRequestsPage from '@/features/adoption-wait/AdoptionWaitRequestsPage';
import ShelterSignupRequestsPage from '@/features/shelter-signups/ShelterSignupRequestsPage';
import EmailContactsAdminPage from '@/features/email-contacts/admin/EmailContactsAdminPage';
import EmailContactDetailPage from '@/features/email-contacts/admin/EmailContactDetailPage';
import EmailImportsAdminPage from '@/features/email-imports/admin/EmailImportsAdminPage';
import EmailImportWizard from '@/features/email-imports/admin/EmailImportWizard';
import EmailCampaignsListPage from '@/features/email-campaigns/admin/EmailCampaignsListPage';
import EmailCampaignEditorPage from '@/features/email-campaigns/admin/EmailCampaignEditorPage';
import EmailCampaignDashboardPage from '@/features/email-campaigns/admin/EmailCampaignDashboardPage';

// Adoption pages
import { AdoptionWizardPage } from '@/features/adoption/wizard/AdoptionWizardPage';
import { ConfirmProfilePage } from '@/features/adoption/pages/ConfirmProfilePage';
import { AdopterDashboardPage } from '@/features/adoption/pages/AdopterDashboardPage';
import { ShelterApplicationsPage, AdminApplicationsPage } from '@/features/adoption/pages/ApplicationsManagementPage';

function ShelterCreateGuard() {
  const { isAdminWithShelter, shelterId } = useAuth();
  if (isAdminWithShelter && shelterId) {
    return <Navigate to={`/shelters/${shelterId}/edit`} replace />;
  }
  return <Outlet />;
}

function SuperAdminGuard() {
  const { isAdmin, isAdminWithShelter } = useAuth();
  if (!isAdmin || isAdminWithShelter) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<AdoptPage />} />
      <Route path="/adopt" element={<AdoptPage />} />
      <Route path="/encontrados" element={<RescuedAnimalsPage />} />
      <Route path="/encontrados/:id" element={<RescuedAnimalDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/auth/handoff" element={<AuthHandoffPage />} />
      <Route path="/webapp/:animalId" element={<WebappEntryPage />} />
      <Route path="/webapp/:animalId/:encodedData" element={<WebappEntryPage />} />
      <Route path="/webapp/:animalId/*" element={<WebappEntryPage />} />
      <Route
        path="/politica-de-privacidad-y-tratamiento-de-datos-personales"
        element={<DataPolicyPage />}
      />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<RoleRoute allow={["admin", "user"]} />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/animals" element={<AnimalsListPage viewMode="available" />} />
          <Route path="/animals/adopted" element={<AnimalsListPage viewMode="adopted" />} />
          {/* Only admin and user can create/edit animals */}
          <Route path="/animals/new" element={<AnimalFormPage />} />
          <Route path="/animals/:id/edit" element={<AnimalFormPage />} />
          <Route path="/rescued-animals" element={<RescuedAnimalsListPage />} />
          <Route path="/rescued-animals/new" element={<RescuedAnimalFormPage />} />
          <Route path="/rescued-animals/:id/edit" element={<RescuedAnimalFormPage />} />
        </Route>

        {/* Lookups are admin-only */}
        <Route element={<RoleRoute allow={["admin"]} />}>
          <Route path="/lookups/species" element={<SpeciesPage />} />
          <Route path="/lookups/sex" element={<SexPage />} />
          <Route path="/lookups/size" element={<SizePage />} />
          <Route path="/lookups/status" element={<StatusPage />} />
          <Route path="/lookups/breeds" element={<div>Breeds - Coming soon</div>} />
          <Route path="/lookups/colors" element={<ColorsPage />} />
        </Route>

        <Route element={<RoleRoute allow={["admin", "user"]} />}>
          <Route path="/shelters" element={<SheltersListPage />} />
          {/* Shelter create admin-only */}
          <Route element={<RoleRoute allow={["admin"]} />}>
            <Route element={<ShelterCreateGuard />}>
              <Route path="/shelters/new" element={<ShelterFormPage />} />
            </Route>
          </Route>
          {/* Shelter edit allowed for admin and user */}
          <Route path="/shelters/:id/edit" element={<ShelterFormPage />} />

          {/* Solicitudes de adopción para refugios */}
          <Route path="/adoptions/manage" element={<ShelterApplicationsPage />} />
          <Route path="/adoption-wait-requests" element={<AdoptionWaitRequestsPage />} />
        </Route>

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route element={<RoleRoute allow={["customer"]} />}>
          <Route path="/adopter/dashboard" element={<AdopterDashboardPage />} />
        </Route>

        {/* Admin: solicitudes globales */}
        <Route element={<RoleRoute allow={["admin"]} />}>
          <Route path="/adoptions/admin" element={<AdminApplicationsPage />} />
          <Route path="/admin/forms" element={<FormTemplatesAdminPage />} />
        </Route>

        {/* Super-admin only */}
        <Route element={<SuperAdminGuard />}>
          <Route path="/admin/banners" element={<BannersAdminPage />} />
          <Route path="/admin/footer" element={<FooterAdminPage />} />
          <Route path="/admin/shelter-signups" element={<ShelterSignupRequestsPage />} />
          <Route path="/admin/email-contacts" element={<EmailContactsAdminPage />} />
          <Route path="/admin/email-contacts/imports" element={<EmailImportsAdminPage />} />
          <Route path="/admin/email-contacts/imports/new" element={<EmailImportWizard />} />
          <Route path="/admin/email-contacts/:id" element={<EmailContactDetailPage />} />
          <Route path="/admin/email-campaigns" element={<EmailCampaignsListPage />} />
          <Route path="/admin/email-campaigns/new" element={<EmailCampaignEditorPage />} />
          <Route path="/admin/email-campaigns/:id" element={<EmailCampaignEditorPage />} />
          <Route path="/admin/email-campaigns/:id/dashboard" element={<EmailCampaignDashboardPage />} />
        </Route>

        {/* Users admin-only */}
        <Route element={<RoleRoute allow={["admin"]} />}>
          <Route path="/users" element={<UsersListPage />} />
          <Route path="/users/new" element={<UserFormPage />} />
          <Route path="/users/:id/edit" element={<UserFormPage />} />
          <Route path="/users" element={<div>Users - Coming soon</div>} />
        </Route>

        {/* Adoption Flow (customer-only) */}
        <Route element={<RoleRoute allow={["customer"]} />}>
          <Route path="/adoption/wizard" element={<AdoptionWizardPage />} />
          <Route path="/adoption/confirm" element={<ConfirmProfilePage />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<SlugRouteResolver />} />
    </Routes>
  );
}
